/**
 * Admin Sidebar Navigation Component
 * 
 * @description
 * Professional sidebar navigation for SaaS Admin and Casino Manager dashboards.
 * Features collapsible sections, active state indicators, and icon support.
 * 
 * @module components/navigation/AdminSidebar
 */

import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface SidebarSection {
  /** Section title */
  title?: string;
  /** Navigation items in this section */
  items: SidebarItem[];
}

export interface SidebarItem {
  /** Display label */
  label: string;
  /** Route path */
  to: string;
  /** Icon component */
  icon?: React.ReactNode;
  /** Badge/notification count */
  badge?: string | number;
}

export interface AdminSidebarProps {
  /** Navigation sections */
  sections: SidebarSection[];
  /** Sidebar title/logo */
  title?: string;
  /** User info displayed at bottom */
  user?: {
    name: string;
    role: string;
    avatar?: string;
  };
  /** Whether to collapse sidebar */
  collapsed?: boolean;
  /** Additional class */
  className?: string;
}

/**
 * Admin Sidebar Navigation Component
 * 
 * @example
 * ```tsx
 * <AdminSidebar
 *   title="Casino SaaS"
 *   sections={[
 *     {
 *       title: "Dashboard",
 *       items: [
 *         { label: "Overview", to: "/saas/dashboard", icon: <HomeIcon /> },
 *         { label: "Tenants", to: "/saas/tenants", icon: <BuildingIcon /> },
 *       ]
 *     },
 *   ]}
 *   user={{ name: "Admin", role: "SaaS Owner" }}
 * />
 * ```
 */
export function AdminSidebar({
  sections,
  title = 'Dashboard',
  user,
  collapsed = false,
  className,
}: AdminSidebarProps): React.ReactElement {
  return (
    <aside
      className={cn(
        'flex flex-col h-full bg-background-secondary border-r border-border',
        collapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      /**
       * Logo/Title Header
       */
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-neon-cyan to-neon-magenta">
          <span className="text-sm font-bold text-background font-display">CS</span>
        </div>
        {!collapsed && (
          <span className="font-display font-semibold text-foreground tracking-wide">
            {title}
          </span>
        )}
      </div>

      /**
       * Navigation Sections
       */
      <nav className="flex-1 overflow-y-auto p-3 space-y-6">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-1">
            {section.title && !collapsed && (
              <h3 className="px-3 py-2 text-xs font-semibold text-foreground-subtle uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            {section.items.map((item) => (
              <SidebarNavItem
                key={item.to}
                item={item}
                collapsed={collapsed}
              />
            ))}
          </div>
        ))}
      </nav>

      /**
       * User Info Footer
       */
      {user && (
        <div className="p-3 border-t border-border">
          <div className={cn(
            'flex items-center gap-3 px-2 py-2 rounded-lg',
            'hover:bg-background-tertiary transition-base'
          )}>
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background-tertiary">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-sm font-medium text-foreground-muted">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {user.name}
                </p>
                <p className="text-xs text-foreground-subtle truncate">
                  {user.role}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

/**
 * Individual Navigation Item
 */
interface SidebarNavItemProps {
  item: SidebarItem;
  collapsed: boolean;
}

function SidebarNavItem({ item, collapsed }: SidebarNavItemProps): React.ReactElement {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'sidebar-item',
          isActive && 'active',
          collapsed && 'justify-center px-2'
        )
      }
      title={collapsed ? item.label : undefined}
    >
      {item.icon && (
        <span className="flex-shrink-0 w-5 h-5">{item.icon}</span>
      )}
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span className="badge-neon text-xs">{item.badge}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

AdminSidebar.displayName = 'AdminSidebar';

/**
 * Icon Components
 * Simple SVG icons for navigation items
 */

export function HomeIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

export function BuildingIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

export function UsersIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

export function SettingsIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

export function ChartIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

export function WalletIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

export function AlertIcon(): React.ReactElement {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}